/**
 * Meta Pixel (Facebook Pixel) integration
 * Provides type-safe wrapper around the fbq global function
 */

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

interface MetaPixelOptions {
  autoConfig?: boolean;
  debug?: boolean;
}

interface EventProperties {
  [key: string]: any;
}

let isInitialized = false;

/**
 * Initialize Meta Pixel
 * @param pixelId - Your Meta Pixel ID
 * @param options - Configuration options
 */
export const initMetaPixel = (
  pixelId: string,
  options: MetaPixelOptions = {}
): void => {
  if (isInitialized) {
    console.warn("Meta Pixel already initialized");
    return;
  }

  if (typeof window === "undefined") {
    return; // SSR guard
  }

  // Initialize fbq function
  const fbq = function () {
    // @ts-ignore
    fbq.callMethod
      ? // @ts-ignore
        fbq.callMethod.apply(fbq, arguments)
      : // @ts-ignore
        fbq.queue.push(arguments);
  };

  if (!window.fbq) {
    window.fbq = fbq;
  }
  // @ts-ignore
  window.fbq.push = fbq;
  // @ts-ignore
  window.fbq.loaded = true;
  // @ts-ignore
  window.fbq.version = "2.0";
  // @ts-ignore
  window.fbq.queue = [];

  // Load the pixel script
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  // Initialize the pixel
  window.fbq("init", pixelId, options);
  window.fbq("track", "PageView");

  isInitialized = true;
};

/**
 * Track a standard or custom event
 * @param eventName - Name of the event (e.g., 'CompleteRegistration', 'Purchase')
 * @param properties - Event properties
 * @param eventId - Optional event ID for deduplication with Conversions API
 */
export const trackMetaEvent = (
  eventName: string,
  properties?: EventProperties,
  eventId?: string
): void => {
  if (typeof window === "undefined" || !window.fbq) {
    console.warn("Meta Pixel not initialized");
    return;
  }

  const params = properties || {};
  if (eventId) {
    params.eventID = eventId;
  }

  window.fbq("track", eventName, params);
};

/**
 * Track a custom event (non-standard event)
 * @param eventName - Name of the custom event
 * @param properties - Event properties
 * @param eventId - Optional event ID for deduplication
 */
export const trackMetaCustomEvent = (
  eventName: string,
  properties?: EventProperties,
  eventId?: string
): void => {
  if (typeof window === "undefined" || !window.fbq) {
    console.warn("Meta Pixel not initialized");
    return;
  }

  const params = properties || {};
  if (eventId) {
    params.eventID = eventId;
  }

  window.fbq("trackCustom", eventName, params);
};

/**
 * Get the Facebook Browser ID (_fbp cookie)
 * This is automatically set by the Meta Pixel
 */
export const getFbp = (): string | null => {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(/(?:^|;)\s*_fbp=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Get or generate the Facebook Click ID (_fbc cookie)
 * This is set when a user clicks a Facebook ad
 */
export const getFbc = (): string | null => {
  if (typeof document === "undefined") return null;

  // First check if _fbc cookie already exists
  const match = document.cookie.match(/(?:^|;)\s*_fbc=([^;]+)/);
  if (match) {
    return decodeURIComponent(match[1]);
  }

  // If no _fbc cookie, try to generate from fbclid URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get("fbclid");

  if (fbclid) {
    // Format: fb.1.timestamp.fbclid
    const timestamp = Date.now();
    const fbc = `fb.1.${timestamp}.${fbclid}`;

    // Set the cookie (180 days expiry)
    const expires = new Date();
    expires.setDate(expires.getDate() + 180);
    document.cookie = `_fbc=${fbc}; expires=${expires.toUTCString()}; path=/`;

    return fbc;
  }

  return null;
};

/**
 * Standard Meta Pixel events
 */
export const META_EVENTS = {
  // E-commerce events
  ADD_PAYMENT_INFO: "AddPaymentInfo",
  ADD_TO_CART: "AddToCart",
  ADD_TO_WISHLIST: "AddToWishlist",
  COMPLETE_REGISTRATION: "CompleteRegistration",
  CONTACT: "Contact",
  CUSTOMIZE_PRODUCT: "CustomizeProduct",
  DONATE: "Donate",
  FIND_LOCATION: "FindLocation",
  INITIATE_CHECKOUT: "InitiateCheckout",
  LEAD: "Lead",
  PURCHASE: "Purchase",
  SCHEDULE: "Schedule",
  SEARCH: "Search",
  START_TRIAL: "StartTrial",
  SUBMIT_APPLICATION: "SubmitApplication",
  SUBSCRIBE: "Subscribe",
  VIEW_CONTENT: "ViewContent",

  // Page events
  PAGE_VIEW: "PageView",
};

export default {
  init: initMetaPixel,
  track: trackMetaEvent,
  trackCustom: trackMetaCustomEvent,
  getFbp,
  getFbc,
  EVENTS: META_EVENTS,
};
